/**
 * 이상한 방 - 발견의 연출 레이어("주스").
 *
 * 이 게임의 보상은 하나다. **"찾았다"의 순간**. 레벨업도 아이템도 없고 5분 동안 열두 번의
 * 발견이 전부라, 그 피드백이 밋밋하면 게임 전체가 밋밋하다. 이 파일이 담당하는 것이 그 0.5초다.
 *
 * ## 이펙트 언어는 하나: 증거 태그 스탬프
 *
 * 파티클도 글로우도 만들지 않는다. 이 게임에는 이미 시각 언어가 있다(현장 감식 - "누가 다녀갔다").
 * 발견 연출은 새 언어가 아니라 **그 언어의 가장 큰 목소리**, 즉 찾아낸 자리에 노란 증거 태그를
 * 탁 붙이는 동작이다. 규칙의 원본은 `gameHud.css`이고, 이 파일은 색도 그림자도 스스로 정하지
 * 않는다(전부 `--gh-*`). 노랑은 폴리스 라인이자 증거 태그의 노랑이라 팔레트는 그대로다.
 *
 * 파티게임이므로 범죄 현장의 무거움은 끌어오지 않는다. **가벼운 탐정 톤**이고, 그래서
 * 페이즈 자막의 만화적 말풍선이 컨셉과 어긋나지 않는다.
 *
 * ## 이 파일이 규칙으로 삼는 넷
 *
 *  1. **수색 중 연출은 0.5초 안에 사라진다.** 아래 길이 상수가 전부 500ms 이하이고,
 *     그 사실을 테스트가 지킨다. 잔상이 남으면 다음 이상한 곳을 찾는 눈을 방해한다.
 *     예외는 셋뿐이고 셋 다 **관찰 시간이 흐르지 않는 순간**에만 뜬다(자막판 · 페이즈 자막 · 리빌의 "아무도 못 봤다" 스탬프).
 *  2. **정리의 권한은 타이머 하나뿐이다.** `animationend`를 듣지 않는다.
 *     `prefers-reduced-motion`에서는 애니메이션이 아예 안 돌고 그러면 그 이벤트도 안 온다 -
 *     움직임을 줄이겠다고 켠 설정이 화면에 영구 잔해를 쌓는 결말이 된다.
 *  3. **`Math.random()`을 쓰지 않는다.** 기울기 같은 연출 난수도 호출 순번에서 뽑는다.
 *     판정에 영향은 없지만, 결정론이 규칙인 프로젝트에서 예외를 하나 두면 그 예외가 자란다.
 *  4. **탭을 막지 않는다.** 레이어는 `pointer-events: none`이고, CSS만 믿지 않고
 *     인라인으로도 건다. 스타일이 안 붙는 사고에서 "안 예쁘다"와 "게임이 멈춘다"는
 *     전혀 다른 무게의 실패다.
 *
 *     **딱 하나 예외가 있다: 페이즈 자막**(`phase`). 그것만 떠 있는 동안 탭을 받고,
 *     받은 탭으로 스스로를 걷는다("아는 사람은 기다릴 이유가 없다"). 이 예외가 안전한 이유는
 *     규칙 2다 - 손가락이 한 번도 닿지 않아도 타이머가 1초 뒤에 노드를 제거하고, 노드가 사라지면
 *     막을 것도 함께 사라진다. 최악의 경우가 "1초 동안 못 쏜다"로 묶여 있다.
 *
 * ## 콤보 규칙은 여기서 계산하지 않는다
 *
 * 배수 사다리(1.0 → 1.2 → … → 3.0)와 끊김 판정은 게임 상태의 몫이다. 이 파일은 받은 값을
 * **그리기만** 한다. 규칙을 여기서 한 번 더 계산하면 규칙이 두 벌이 되고, 둘이 갈리는 날
 * 화면은 ×1.4라고 말하는데 점수는 1.2로 붙는다. `comboStep`은 오디오와 같은 규약으로
 * **0부터**다(콤보 1단계 = 0).
 */

import { prefersReducedMotion } from '../core/env'
import { t } from '../i18n'

import { phaseStampKey } from './cosmeticVars'
import { createElement, restartAnimation } from './dom'

import './juice.css'

/* --- 길이 ---------------------------------------------------------------------- */

/*
 * 수색 중에 뜨는 넷은 전부 0.5초 이하다. 그 약속을 테스트가 감시한다.
 * 자막(STAMP)·점수(SCORE)·오답 플래시(FLASH)·콤보 끊김(BREAK).
 */
const STAMP_MS = 460
const SCORE_MS = 500
const FLASH_MS = 260
const BREAK_MS = 480

/** 오답 흔들림(진폭은 CSS의 `--jc-shake`)과 HUD 마이크로 펀치. 감각이라 더 짧다. */
const SHAKE_MS = 220
const PUNCH_MS = 170

/*
 * 라운드가 끝난 뒤에만 뜨는 자막판. 0.5초 규칙 밖이다 -
 * 관찰할 방이 더 없으므로 가릴 것도 없다.
 */
const FINALE_MS = 1500
const PERFECT_MS = 1900

/**
 * 페이즈 시작 자막이 화면에 머무는 시간. 0.5초 규칙 밖인 두 번째이자 마지막 예외다.
 *
 * 여기서 화면을 크게 써도 되는 이유는 **이 자막이 도는 동안 제한 시간이 흐르지 않기 때문이다**
 * (완료 콜백 계약은 `JuiceHandle.phase` 주석에 있다). 시간을 안 잡아먹으니 크기는 마음껏 쓴다.
 *
 * 그럼에도 1초대에 묶어 둔다. 10단계 × 재도전이면 이 자막을 한 세션에 수십 번 본다.
 * 세 번째로 볼 때부터는 정보가 아니라 리듬이고, 리듬이 길면 그건 그냥 로딩 화면이다.
 * 아는 사람을 위한 문은 따로 있다 - 탭하면 즉시 걷힌다.
 */
const PHASE_MS = 1080

/**
 * "아무도 못 봤다" 스탬프(여럿이 리빌, 로드맵 M5)가 머무는 시간. 0.5초 규칙 밖의 **세 번째 예외**다.
 *
 * 예외인 이유는 앞의 둘과 같다 - **관찰 시간이 흐르지 않는 순간**에만 뜬다. 리빌은 수색이 아니다. 찾을 방이 더 없고
 * 화면의 목적이 "다음 이상한 곳을 찾게 하는 것"에서 "저걸 아무도 못 봤다고???"(컨셉시트 5절 6번)로 바뀐 뒤다.
 * 그럼에도 1.4초에 묶는다 - 리빌 한 스텝이 2.5초이고 그 안에 깜빡 교차가 세 번 지나가야 하므로, 스탬프가 스텝을 넘겨 살면
 * 다음 스텝의 얼굴과 겹친다. 파티 리빌은 이것을 **마지막 미발견 한 번**에만 부른다(`game/matchSessionReveal.ts`).
 */
const NOBODY_SAW_MS = 1400

/* --- 세기 ---------------------------------------------------------------------- */

/*
 * 콤보가 오를수록 자막이 커지고 HUD가 더 세게 숨을 쉰다. 둘 다 `BASE + min(단계, MAX) * STEP`
 * 이라는 같은 모양이고, 둘 다 상한이 있는 이유도 같다 - 12연속에서 화면을 덮을 크기가 되면
 * 그때부터 보상이 방해가 된다. 자막 상한 1.4, 펀치 상한 1.032("미세하게"의 실제 값).
 */
const POP_BASE = 1
const POP_STEP = 0.05
const POP_MAX_STEPS = 8

const PUNCH_BASE = 1.012
const PUNCH_STEP = 0.0025
const PUNCH_MAX_STEPS = 8

/**
 * 자막 문구가 바뀌는 콤보 단계. 넷으로 나눈 이유는 열두 번의 발견에 다섯 개 이상의 문구를 두면
 * 한 판에 한 번씩만 보이는 문구가 생겨 "그 말을 본 적 있다"는 기억이 안 남기 때문이다.
 */
const TIER_STEPS = [2, 4, 7] as const

/** 배수 칩을 띄우는 최소값. 1.0은 자랑할 것이 없다. */
const MULTIPLIER_MIN = 1.05

/** 배수 소수 자리. `×1.4`. */
const MULTIPLIER_DIGITS = 1

/* --- 배치 ---------------------------------------------------------------------- */

/**
 * 기울기 표. 호출 순번을 이 표의 길이로 나눈 나머지가 각도다.
 * 난수처럼 보이되 결정론이고, 표 길이가 6이라 연타해도 같은 각이 붙어 보이지 않는다.
 */
const TILT_DEG = [-4, 3, -6, 5, -3, 6] as const

/*
 * 화면 안으로 물리는 여백. 좌우는 **최소값**일 뿐이고 실제로는 자막의 절반 폭이 이긴다(`place`).
 * 위가 유독 큰 이유는 자막탑이 탭 지점 **위로만** 세 층(점수·자막·칩) 쌓이기 때문이며,
 * 근거 수치는 CSS의 `--jc-score-lift`(96px) + 점수 글자 높이다.
 * GUTTER는 자막 끝과 화면 끝 사이의 숨 - 0이면 글자가 모서리에 닿아 잘린 것처럼 보인다.
 */
const EDGE_X_PX = 40
const EDGE_GUTTER_PX = 10
const EDGE_TOP_PX = 124
const EDGE_BOTTOM_PX = 64

/** jsdom처럼 레이아웃이 없는 환경의 화면 크기 대체값. */
const FALLBACK_W = 360
const FALLBACK_H = 640

/**
 * 동시에 떠 있을 수 있는 연출 노드 수. 넘으면 가장 오래된 것부터 즉시 치운다.
 * 연타로 노드가 쌓이면 그것 자체가 "화면을 덮지 마라"를 어기는 길이다.
 */
const MAX_LIVE_NODES = 6

/* --- 뾰족뾰족 말풍선 ------------------------------------------------------------- */

/*
 * 페이즈 자막이 들어앉는 만화식 폭발형 말풍선(starburst)을 `clip-path: polygon()`으로 그린다.
 * 이미지가 필요 없고, 말풍선이 글자를 따라 커지며, 색은 노란 판 하나라 팔레트를 안 늘린다.
 *
 * 좌표는 백분율이라 상자의 가로세로 비를 그대로 따른다 - 가로로 넓은 상자에서는 별이 가로로
 * 늘어나는데, 그게 두 줄짜리 문구에도 맞는 자연스러운 모양이다.
 */

/** 톱니 개수. **짝수여야** 바깥 뿔과 안쪽 골이 어긋나지 않고 한 바퀴에 정확히 닫힌다. */
const BURST_SPIKES = 24

/**
 * 골의 깊이(바깥 윤곽 대비). **이 숫자가 곧 글자의 안전 구역이다.**
 *
 * 골이 깊을수록 만화 같아지지만, 안쪽 윤곽이 그만큼 작아져 글자 모서리가 골에 물린다.
 * 0.16이면 안쪽 윤곽이 상자의 84%이고, `juice.css`의 말풍선 여백이 그 84%를 근거로 잡혀 있다
 * (실제 글꼴 폭으로 검산한 식이 그쪽 주석에 있다). 이 값을 키우면 **반드시 여백도 같이 키워라.**
 */
const BURST_DEPTH = 0.16

/**
 * 윤곽의 각짐(초타원 지수). 2면 타원, 커질수록 상자를 꽉 채우는 둥근 사각형에 가까워진다.
 *
 * **타원(2)으로는 안 된다.** 이 말풍선에 들어가는 문구는 가로로 길고 세로로 낮은데
 * (영어 "MEMORIZE!"는 글꼴 실측 6.09em짜리 한 덩어리라 줄바꿈도 안 된다),
 * 타원의 안쪽은 그런 글자 상자의 좌우 끝을 정확히 잘라 먹는다. 실제로 계산해 보면 딱 1.005 -
 * 아슬아슬하게 넘친다. 4로 두면 같은 문구가 0.72에서 여유 있게 들어앉는다.
 */
const BURST_SQUARENESS = 4

/**
 * 뿔의 각도를 흔드는 표(도). 뿔이 정확히 등간격이면 말풍선이 아니라 톱니바퀴로 읽힌다.
 *
 * **길이가 아니라 각도를 흔드는 이유가 있다.** 뿔을 짧게 만들어 변화를 주면, 초타원 윤곽은
 * 대각선 쪽이 축 쪽보다 19% 멀기 때문에 짧아진 뿔이 옆 골보다 안쪽으로 들어가 버린다 -
 * 뿔이 골로 읽히고 그 자리만 이가 빠진 것처럼 보인다. 각도만 흔들면 모든 점의 크기가 그대로라
 * 별 모양이 절대 무너지지 않고, 이의 폭만 들쭉날쭉해져 손으로 그린 맛이 난다.
 *
 * 표의 길이 7은 톱니 24와 서로소다. 약수를 고르면 같은 무늬가 네 번 도는 것이 눈에 보인다.
 * 흔드는 폭은 반칸(7.5도)보다 작아야 뿔이 옆 골을 넘어가지 않는다.
 */
const BURST_JITTER_DEG = [0, 2.4, -1.6, 1.1, -2.7, 0.6, -0.9] as const

/** 첫 뿔의 각도. 정수리에서 시작해야 좌우가 대칭으로 읽힌다. */
const BURST_START_DEG = -90

/** 좌표 소수 자리. 점이 40개라 자릿수를 열어 두면 문자열만 길어진다. */
const BURST_DIGITS = 2

/** 도 → 라디안. */
const DEG_TO_RAD = Math.PI / 180

/** 중심 50%에서 반지름 0.5가 곧 상자의 절반이다. */
const HALF = 0.5
const PERCENT = 100
const CENTER_PCT = 50

/**
 * 말풍선이 화면 끝에 닿지 않게 남기는 비율. 뿔 끝이 모서리에 붙으면 잘린 것처럼 보인다.
 * 폭 375px에서 양쪽 11px씩으로, HUD의 `--gh-edge`(12px)와 사실상 같은 숨이다.
 */
const PHASE_FIT_INSET = 0.94

/** 초타원 한 축. `sign(v) * |v|^(2/n)`이 `|x|^n + |y|^n = 1`을 그린다. */
function superellipse(value: number): number {
  return Math.sign(value) * Math.abs(value) ** (2 / BURST_SQUARENESS)
}

/**
 * 말풍선 윤곽 한 벌. 호출부는 이 문자열을 `--jc-burst`에 심고 CSS가 `clip-path`로 쓴다.
 *
 * 짝수 순번이 뿔(윤곽 위), 홀수 순번이 골(윤곽에서 `BURST_DEPTH`만큼 안쪽)이다.
 * 뿔에만 각도 흔들림을 얹으므로 골은 언제나 제자리이고, 그래서 안쪽 윤곽 -
 * 즉 글자의 안전 구역 - 은 흔들림과 무관하게 일정하다.
 *
 * 좌표가 백분율이라 윤곽은 상자의 가로세로 비를 그대로 따른다 - 가로로 넓은 말풍선에서는
 * 별도 가로로 늘어나는데, 그것이 글자를 담는 모양으로도 맞다.
 */
export function burstPolygon(): string {
  const total = BURST_SPIKES * 2
  const points: string[] = []

  for (let i = 0; i < total; i += 1) {
    const isTip = i % 2 === 0
    const radius = isTip ? HALF : HALF * (1 - BURST_DEPTH)
    const jitter = isTip ? (BURST_JITTER_DEG[(i / 2) % BURST_JITTER_DEG.length] ?? 0) : 0
    const angle = (BURST_START_DEG + (i * 360) / total + jitter) * DEG_TO_RAD
    const x = CENTER_PCT + superellipse(Math.cos(angle)) * radius * PERCENT
    const y = CENTER_PCT + superellipse(Math.sin(angle)) * radius * PERCENT
    points.push(`${x.toFixed(BURST_DIGITS)}% ${y.toFixed(BURST_DIGITS)}%`)
  }

  return `polygon(${points.join(', ')})`
}

/**
 * 말풍선이 화면을 넘지 않도록 줄일 배율. 넘치지 않으면 1이다(키우지는 않는다).
 *
 * **글꼴 크기가 아니라 말풍선 전체를 줄인다.** 글자만 줄이면 여백과 줄바꿈 지점이 함께 움직여
 * "줄였더니 줄바꿈이 달라지고, 달라진 줄바꿈 때문에 다시 줄여야 하는" 되먹임이 생긴다.
 * 통째로 줄이면 안쪽 비율이 그대로라 한 번의 계산으로 끝나고, 글자가 톱니에 물리지 않는다는
 * 보장(`juice.css`의 여백 계산)도 배율과 무관하게 유지된다.
 *
 * 레이아웃이 없는 환경(jsdom)에서는 잴 것이 없으므로 손대지 않는다.
 */
export function fitScale(
  available: { readonly width: number; readonly height: number },
  natural: { readonly width: number; readonly height: number },
): number {
  if (natural.width <= 0 || natural.height <= 0) return 1
  if (available.width <= 0 || available.height <= 0) return 1
  return Math.min(
    1,
    (available.width * PHASE_FIT_INSET) / natural.width,
    (available.height * PHASE_FIT_INSET) / natural.height,
  )
}

/* --- 타입 ---------------------------------------------------------------------- */

/** 한 발의 히트를 화면에 어떻게 터뜨릴 것인가. */
export type HitJuice = Readonly<{
  /** 콤보 단계. **0부터**다(오디오의 `sound('hit', step)`과 같은 규약). */
  comboStep: number
  /** 지금 걸린 배수. 계산은 `game/combo.ts`가 하고 여기서는 찍기만 한다. */
  multiplier?: number
  /**
   * 배수가 상한(3.0)에 닿았는가. `combo.isComboAtCap`을 그대로 넘긴다.
   *
   * 닿은 순간부터 칩이 **노란 판에 먹색 숫자**로 뒤집힌다. 문구를 더하지 않고 색만 뒤집는 이유는
   * 천장이 정보가 아니라 감각이기 때문이다 - 0.4초짜리 연출에 읽을 글자를 하나 더 얹으면
   * 아무도 안 읽고, 뒤집힌 색은 안 읽어도 보인다.
   */
  atCap?: boolean
  /** 이번 한 발로 얻은 점수. 없으면 점수 팝업을 띄우지 않는다. */
  score?: number
  /**
   * 큰 자막을 띄울 것인가. 기본은 띄운다.
   * `gh-mark`의 라벨과 위아래로 쌓이는 것이 싫은 호출부만 끄면 된다.
   */
  caption?: boolean
  /**
   * **남이** 찾았다 - 그 사람의 이름표(여럿이, 로드맵 M2). 있으면 자막탑 대신 이름 하나가 그 자리에 찍히고
   * 배수 칩·점수·계기판 펀치는 **전부 생략**한다. 남의 발견은 내 보상이 아니라 정보다 - 내 화면이 남의 점수로
   * 흔들리면 관찰이 끊기고, 콤보 칩이 뜨면 "내 콤보인가"가 된다. 문구는 사전이 아니라 **이름 그대로**다 -
   * 사람 이름은 번역 대상이 아니다. 0.5초 규칙은 그대로다(`STAMP_MS`).
   */
  by?: string
}>

export type MissJuice = Readonly<{
  /** 이 한 발로 콤보가 끊겼는가. 끊겼다면 그것도 자막으로 알린다. */
  comboBroken?: boolean
  /** 가장자리 플래시를 띄울 것인가. 기본은 띄운다(`gh-damage`와 겹치면 끈다). */
  flash?: boolean
}>

export type FinaleJuice = Readonly<{
  /** 무오답 전량 발견. 이 게임에서 가장 큰 보상이다. */
  perfect?: boolean
  /** 찾은 개수. 자막판 아랫줄에 들어간다. */
  foundCount?: number
  /** 퍼펙트 보너스 점수. 있으면 아랫줄이 보너스를 말한다. */
  bonus?: number
}>

/**
 * 페이즈 시작 자막의 종류. 값이 곧 사전 키의 가운데 토막이다(`phase.<이름>.stamp`).
 *
 * 둘은 **짝으로 읽혀야 한다.** 같은 자리 · 같은 크기 · 같은 리듬으로 들어와서
 * "외운 방에 누가 손을 댔다"가 설명 없이 전달되는 것이 이 자막 두 장의 목적이다.
 * 다른 것은 무게뿐이다 - 수색 쪽이 한 박자 무겁게 떨어진다(`juice.css`의 `--jc-phase-from`).
 */
export type PhaseStamp = 'watch' | 'hunt'

export type PhaseStampOptions = Readonly<{
  /**
   * 자막을 띄우기 전에 기다릴 시간. **타이머는 이 레이어가 갖는다** - 호출부가 `setTimeout`으로
   * 미루면 라운드가 중간에 접힌 뒤에 자막이 뒤늦게 튀어나오고, 그 타이머를 걷을 사람이 없다.
   * 여기 맡기면 `clear`·`setVisible(false)`·`dispose`가 예약까지 함께 데려간다.
   *
   * 수색 자막이 이걸 쓴다. 방 전환(warp) 이펙트가 도는 동안 자막이 겹치면 둘 다 안 읽힌다.
   */
  delayMs?: number
  /**
   * 자막이 **완전히 걷힌 뒤** 부른다. 외우기 제한 시간은 여기서 시작한다.
   *
   * **반드시, 정확히 한 번 불린다.** 스스로 사라졌든 · 탭으로 걷혔든 · `clear`나 `dispose`가
   * 도중에 데려갔든 · 애초에 레이어가 꺼져 있어 자막이 안 떴든 결과는 같다.
   * 이 약속이 없으면 "연출이 취소되는 바람에 제한 시간이 영영 시작되지 않는" 잠금이 생기는데,
   * 그건 연출 버그가 아니라 게임이 멈추는 버그다. 취소 경로에서는 그 자리에서 동기로 불린다.
   */
  onDone?: () => void
}>

export type JuiceOptions = Readonly<{
  mount: HTMLElement
  /**
   * 마이크로 펀치를 걸 엘리먼트. 보통 게임 HUD 루트(`.gh-root`)다.
   * **씬 카메라를 CSS로 건드리지 않는다** - 씬은 Three.js이고, 캔버스에 transform을 걸면
   * 렌더 해상도와 픽셀이 어긋난다. 타격감은 계기판이 한 번 숨을 쉬는 것으로 낸다.
   */
  punchTarget?: HTMLElement | null
  /**
   * 오답에서 흔들 엘리먼트. 기본은 `punchTarget`.
   * 방까지 흔들고 싶으면 캔버스를 감싼 래퍼를 넘겨라(캔버스 자체는 권하지 않는다).
   */
  shakeTarget?: HTMLElement | null
}>

export type JuiceHandle = Readonly<{
  /** 맞은 지점에 자막 스탬프 · 배수 칩 · 점수 팝업을 터뜨린다. */
  hit: (clientX: number, clientY: number, juice: HitJuice) => void
  /** 오답. 붉은 가장자리 플래시 + 미세한 흔들림, 콤보가 끊겼으면 그 자막까지. */
  miss: (clientX: number, clientY: number, juice?: MissJuice) => void
  /** 전량 발견 자막판. 라운드가 끝난 뒤에만 부른다. */
  finale: (juice?: FinaleJuice) => void
  /**
   * "아무도 못 봤다" 대형 스탬프(여럿이 리빌, 로드맵 M5). **리빌 중에만** 부른다 - 수색 중에 부르면 0.5초 규칙을 어긴다.
   *
   * 자막판(`finale`)과 달리 워시를 깔지 않고 화면 **아래쪽**에 선다. 리빌 카메라가 그 한 건을 화면 가운데에 놓고 깜빡 교차를
   * 돌리는 중이라, 가운데를 덮으면 "무엇을 아무도 못 봤는지"가 사라진다 - 스탬프가 자기 주어를 가리는 셈이다.
   * 문구는 사전(`juice.nobodySaw.*`)이 정한다. 노드는 하나뿐이고 예약도 하나다(`slots`) - 연달아 불러도 쌓이지 않는다.
   */
  nobodySaw: () => void
  /**
   * 페이즈 시작 자막. 화면 한가운데에 뾰족뾰족 말풍선을 크게 터뜨린다.
   *
   * 문구는 사전이 정한다(`phase.watch.stamp` · `phase.hunt.stamp`, 뽑은 세트가 있으면 `.<꾸밈 id>`가 붙는다).
   * 이 파일에는 문구가 없다.
   *
   * **이 자막이 떠 있는 동안 제한 시간이 흘러서는 안 된다.** 그래서 `onDone`이 있고,
   * 호출부는 그 콜백에서 시계를 시작한다. 자막이 도는 1초를 플레이어가 손해 볼 이유가 없다.
   * 콜백은 취소된 경우에도 반드시 불린다(위 `PhaseStampOptions.onDone` 참고).
   *
   * 앞의 자막이 아직 떠 있으면 그것을 먼저 걷는다(그 자리에서 앞의 `onDone`이 불린다).
   * 그래서 몇 번을 연달아 불러도 화면에 남는 말풍선은 언제나 하나뿐이다.
   */
  phase: (which: PhaseStamp, options?: PhaseStampOptions) => void
  /** 진행 중인 연출을 전부 즉시 치운다. */
  clear: () => void
  setVisible: (visible: boolean) => void
  dispose: () => void
}>

/* --- 문구 ---------------------------------------------------------------------- */

/*
 * 문구는 전부 사전에서 온다(`i18n/ko.ts`의 `juice.*`). 이 파일에 한국어 문자열이 없는 이유가
 * 그것이다 - 자막을 여기에 한 벌 더 두면 두 벌이 조용히 갈리고, 갈린 쪽은 번역에서 빠진다.
 */

/* --- 순수 계산 ----------------------------------------------------------------- */

/** 음수·NaN·소수를 0 이상의 정수로 눕힌다. 콤보 값은 바깥에서 오므로 방어한다. */
function safeStep(step: number): number {
  return Number.isFinite(step) ? Math.max(0, Math.floor(step)) : 0
}

/** 콤보 단계 → 자막 문구 단계(0~3). */
export function comboTier(step: number): number {
  const safe = safeStep(step)
  let tier = 0
  for (const threshold of TIER_STEPS) {
    if (safe >= threshold) tier += 1
  }
  return tier
}

/** 콤보 단계 → 자막 배율. 단조 증가하다 `POP_MAX_STEPS`에서 멈춘다. */
export function popScale(step: number): number {
  return POP_BASE + Math.min(safeStep(step), POP_MAX_STEPS) * POP_STEP
}

/** 콤보 단계 → HUD 펀치 배율. 같은 모양, 훨씬 작은 폭. */
export function punchScale(step: number): number {
  return PUNCH_BASE + Math.min(safeStep(step), PUNCH_MAX_STEPS) * PUNCH_STEP
}

/** 호출 순번 → 기울기(deg). 난수가 아니라 표의 순환이다. */
export function stampTilt(index: number): number {
  const safe = safeStep(index)
  return TILT_DEG[safe % TILT_DEG.length] ?? 0
}

/** `×1.4`. 곱셈 기호는 U+00D7 - 알파벳 x를 쓰면 수치가 글자로 읽힌다. */
export function formatMultiplier(value: number): string {
  const safe = Number.isFinite(value) ? value : 1
  return `×${safe.toFixed(MULTIPLIER_DIGITS)}`
}

/** `+120`. 자리 구분 기호를 넣지 않는다(로케일마다 갈려 결정론이 깨진다). */
export function formatScore(value: number): string {
  const safe = Number.isFinite(value) ? Math.round(value) : 0
  return safe < 0 ? String(safe) : `+${safe}`
}

/* --- 팩토리 -------------------------------------------------------------------- */

type LiveNode = { readonly node: HTMLElement; timer: number }

/** 노드를 새로 만들지 않는 다섯 연출의 이름표. 각자 예약을 하나씩만 갖는다. */
type SlotName = 'flash' | 'board' | 'punch' | 'shake' | 'nobody'

export function createJuice(options: JuiceOptions): JuiceHandle {
  const root = createElement('div', 'jc-root')
  /*
   * 이 레이어는 순수 장식이다. 사격 결과의 스크린리더 안내는 게임 HUD의 알림 영역이
   * 이미 맡고 있으므로, 여기서 또 읽으면 한 발마다 같은 말이 두 번 들린다.
   */
  root.setAttribute('aria-hidden', 'true')
  /* CSS를 못 믿는 자리. 이 한 줄이 "이펙트가 탭을 삼킨다"는 최악의 실패를 막는다. */
  root.style.pointerEvents = 'none'

  const flash = createElement('div', 'jc-flash')
  const board = createElement('div', 'jc-board')
  const boardBand = createElement('div', 'jc-board__band')
  const boardTitle = createElement('div', 'jc-board__title')
  const boardSub = createElement('div', 'jc-board__sub')
  /* 순서가 곧 배치다. 띠는 제목 **아래** 줄이다(그 이유는 `juice.css`의 `.jc-board__band`). */
  board.append(boardTitle, boardBand, boardSub)

  /*
   * 페이즈 자막. 노드를 미리 하나 만들어 두고 켜고 끄기만 한다(자막판과 같은 방식).
   * 매번 새로 만들면 "연달아 부르면 말풍선이 쌓인다"가 언제든 가능해지는데,
   * 이 자막은 화면을 통째로 쓰므로 두 장이 겹치는 순간이 곧 최악의 실패다.
   *
   * 판(`__plate`)이 글자와 형제인 이유: `clip-path`로 오린 판 **위에** 글자를 얹어야
   * 글자가 톱니에 함께 잘리지 않는다. 판을 부모로 삼으면 글자도 같이 오려진다.
   */
  const phaseLayer = createElement('div', 'jc-phase')
  const phaseBurst = createElement('div', 'jc-phase__burst')
  const phasePlate = createElement('div', 'jc-phase__plate')
  const phaseText = createElement('div', 'jc-phase__text')
  phaseBurst.append(phasePlate, phaseText)
  phaseLayer.append(phaseBurst)
  /* 켜져 있는 동안에만 인라인으로 `auto`가 된다(`showPhase`). 기본값은 언제나 막지 않는 쪽이다. */
  phaseLayer.style.pointerEvents = 'none'

  /* 톱니 윤곽은 상수에서 나온다. 한 번 계산해 CSS에 넘기고, 그 뒤로는 CSS의 것이다. */
  root.style.setProperty('--jc-burst', burstPolygon())

  /*
   * "아무도 못 봤다" 스탬프(여럿이 리빌). 자막판과 같은 방식으로 노드를 미리 하나 두고 켜고 끈다 -
   * 화면을 크게 쓰는 연출은 두 장이 겹치는 순간이 곧 최악의 실패라 새로 만들지 않는다.
   */
  const nobody = createElement('div', 'jc-nobody')
  const nobodyTitle = createElement('div', 'jc-nobody__title')
  const nobodySub = createElement('div', 'jc-nobody__sub')
  nobody.append(nobodyTitle, nobodySub)

  root.append(flash, board, nobody, phaseLayer)
  options.mount.append(root)

  const shakeTarget = options.shakeTarget ?? options.punchTarget ?? null

  /** 떠 있는 연출 노드들. 오래된 것이 앞에 온다. */
  const live: LiveNode[] = []
  /** 노드가 없는 연출(플래시·자막판·펀치·흔들림)의 예약. 이름표당 하나뿐이다. */
  const slots = new Map<SlotName, number>()
  /** 기울기 표를 돌리는 순번. 이것이 이 파일의 유일한 "난수"다. */
  let tiltIndex = 0
  let visible = true

  /* --- 타이머 ------------------------------------------------------------- */

  /**
   * 이름표 하나에 예약 하나. 같은 이름의 예약이 다시 들어오면 **앞의 것을 취소한다.**
   *
   * 이게 없으면 연타에서 조용히 깨진다. 오답을 빠르게 두 번 내면 첫 번째의 정리 타이머가
   * 두 번째 플래시가 한창일 때 도착해 `is-on`을 떼어 버린다. 두 번째 오답만 유독 흐릿하게
   * 지나가는데, 그 원인은 화면만 봐서는 절대 안 보인다.
   * 노드를 새로 만드는 자막(`spawn`)은 노드마다 타이머가 따로라 이 문제가 없다.
   */
  function schedule(name: SlotName, action: () => void, delayMs: number): void {
    const previous = slots.get(name)
    if (previous !== undefined) window.clearTimeout(previous)
    slots.set(
      name,
      window.setTimeout(() => {
        slots.delete(name)
        action()
      }, delayMs),
    )
  }

  function clearSlots(): void {
    for (const id of slots.values()) window.clearTimeout(id)
    slots.clear()
  }

  /* --- 노드 수명 ---------------------------------------------------------- */

  function drop(entry: LiveNode): void {
    window.clearTimeout(entry.timer)
    entry.node.remove()
    const at = live.indexOf(entry)
    if (at >= 0) live.splice(at, 1)
  }

  function spawn(node: HTMLElement, lifetimeMs: number, clientX: number, clientY: number): void {
    while (live.length >= MAX_LIVE_NODES) {
      const oldest = live[0]
      if (oldest === undefined) break
      drop(oldest)
    }

    /*
     * 붙이고 → 재고 → 애니메이션을 켠다. 순서가 규칙이다. 폭은 붙은 뒤에만 잴 수 있고,
     * 재는 행위가 곧 리플로우인데 그 리플로우는 어차피 애니메이션 재시작에 필요하다
     * (`restartAnimation`). 한 발에 한 번뿐이라 매 프레임 도는 비용이 아니다.
     */
    root.append(node)
    place(node, clientX, clientY)
    node.classList.add('is-on')

    const entry: LiveNode = { node, timer: 0 }
    entry.timer = window.setTimeout(() => {
      drop(entry)
    }, lifetimeMs)
    live.push(entry)
  }

  /* --- 배치 --------------------------------------------------------------- */

  /**
   * 한 축을 화면 안으로 물린다.
   * 여백이 화면의 절반을 넘으면(작은 화면 × 최대 콤보 자막) 물릴 자리가 없으므로 가운데로 보낸다 -
   * 이때 한쪽으로 붙이면 반대쪽이 통째로 잘린다.
   */
  function clampAxis(value: number, size: number, margin: number): number {
    if (margin * 2 >= size) return size / 2
    if (!Number.isFinite(value)) return margin
    return Math.min(Math.max(value, margin), size - margin)
  }

  /**
   * 눌린 좌표를 화면 안으로 물린다. **노드가 붙은 뒤에 부른다** - 폭을 재야 하기 때문이다.
   *
   * 폭을 재는 이유: 최대 콤보의 자막은 폰 화면 폭의 절반을 넘는다. 고정 여백으로만 물리면
   * 오른쪽 끝을 쏜 순간 자막의 절반이 화면 밖으로 나가 잘린 채 뜬다.
   *
   * **잰 폭에 `--jc-pop`을 곱하는 것이 핵심이다.** `offsetWidth`는 레이아웃 폭이라
   * `transform: scale()`을 세지 않는다. 콤보 최대에서 자막은 1.4배로 그려지므로,
   * 곱하지 않으면 정확히 가장 큰 자막에서만 잘리는 - 즉 가장 중요한 순간에만 실패하는 - 버그가 된다.
   *
   * 위아래 여백이 좌우와 다른 이유는 자막탑이 탭 지점 **위로만** 쌓이기 때문이다.
   */
  function place(node: HTMLElement, clientX: number, clientY: number): void {
    const width = root.clientWidth || window.innerWidth || FALLBACK_W
    const height = root.clientHeight || window.innerHeight || FALLBACK_H
    const pop = Number.parseFloat(node.style.getPropertyValue('--jc-pop')) || 1
    const marginX = Math.max(EDGE_X_PX, (node.offsetWidth * pop) / 2 + EDGE_GUTTER_PX)
    node.style.left = `${clampAxis(clientX, width, marginX)}px`
    node.style.top = `${clampVertical(clientY, height)}px`
  }

  /** 세로는 위아래 여백이 달라 `clampAxis`를 못 쓴다. */
  function clampVertical(value: number, height: number): number {
    if (EDGE_TOP_PX + EDGE_BOTTOM_PX >= height) return height / 2
    if (!Number.isFinite(value)) return EDGE_TOP_PX
    return Math.min(Math.max(value, EDGE_TOP_PX), height - EDGE_BOTTOM_PX)
  }

  /* --- 남의 엘리먼트에 거는 두 연출 ----------------------------------------- */

  /*
   * 펀치와 흔들림은 같은 `transform`을 다투므로 **한 엘리먼트에 하나씩만** 건다.
   * 히트는 펀치, 오답은 흔들림 - 둘이 동시에 일어나는 입력은 없다.
   */

  function punch(step: number): void {
    const target = options.punchTarget
    if (target === null || target === undefined) return
    if (prefersReducedMotion()) return

    target.classList.remove('jc-punch')
    restartAnimation(target)
    target.style.setProperty('--jc-punch', punchScale(step).toFixed(3))
    target.classList.add('jc-punch')
    schedule('punch', () => target.classList.remove('jc-punch'), PUNCH_MS)
  }

  function shake(): void {
    if (shakeTarget === null) return
    if (prefersReducedMotion()) return

    shakeTarget.classList.remove('jc-shaking')
    restartAnimation(shakeTarget)
    shakeTarget.classList.add('jc-shaking')
    schedule('shake', () => shakeTarget.classList.remove('jc-shaking'), SHAKE_MS)
  }

  function stopBorrowed(): void {
    options.punchTarget?.classList.remove('jc-punch')
    shakeTarget?.classList.remove('jc-shaking')
  }

  /* --- 자막 스탬프 --------------------------------------------------------- */

  function stampNode(caption: string, modifier: string, pop: number): HTMLDivElement {
    const node = createElement('div', `jc-stamp ${modifier}`)
    node.style.setProperty('--jc-pop', pop.toFixed(3))
    node.style.setProperty('--jc-tilt', `${stampTilt(tiltIndex)}deg`)
    tiltIndex += 1
    node.append(createElement('div', 'jc-stamp__caption', caption))
    return node
  }

  /* --- 페이즈 시작 자막 ------------------------------------------------------ */

  /*
   * 이 자막만 `slots`를 안 쓴다. 이름표 하나에 예약 하나라는 규칙은 같지만,
   * 여기에는 예약과 함께 **반드시 불러야 할 콜백**이 딸려 있어서 취소 경로가 다르다.
   * `schedule`에 얹으면 "타이머만 조용히 취소"가 가능해지고, 그게 곧 제한 시간이
   * 영영 시작되지 않는 잠금이다. 그래서 타이머와 콜백을 한 쌍으로 묶어 따로 둔다.
   */
  let phaseTimer = 0
  let phaseDone: (() => void) | null = null

  /**
   * 자막을 걷고 **완료 콜백을 정확히 한 번 부른다.** 이 파일에서 가장 조심스러운 함수다.
   *
   * 순서가 규칙이다. 콜백을 부르기 **전에** 상태를 전부 비운다 - 콜백이 그 자리에서
   * `phase`를 다시 부르는 경우가 이론상 가능하고, 그때 뒤늦게 이 함수의 나머지 줄이
   * 새 예약을 지우면 자막 한 장이 통째로 증발한다.
   *
   * 예약이 없으면(자막이 안 떠 있으면) 아무 일도 하지 않는다. 두 번 불려도 콜백은 한 번뿐이다.
   */
  function endPhase(): void {
    if (phaseTimer !== 0) {
      window.clearTimeout(phaseTimer)
      phaseTimer = 0
    }
    phaseLayer.classList.remove('is-on')
    /* 노드가 남아 있어도 탭을 막지 않는 상태로 되돌린다. CSS가 안 붙는 사고까지 덮는 한 줄이다. */
    phaseLayer.style.pointerEvents = 'none'

    const done = phaseDone
    phaseDone = null
    done?.()
  }

  /**
   * 말풍선을 화면 안에 물린다. **켠 직후, 페인트 전에** 부른다.
   *
   * `offsetWidth`를 읽는 것이 곧 강제 리플로우이고, 그 리플로우는 어차피 애니메이션을
   * 처음부터 돌리는 데 필요한 것이라 값이 두 번 나가지 않는다(`spawn`과 같은 수법).
   * 한 라운드에 두 번 도는 계산이므로 매 프레임 비용을 걱정할 자리가 아니다.
   */
  function fitPhase(): void {
    const scale = fitScale(
      { width: phaseLayer.clientWidth, height: phaseLayer.clientHeight },
      { width: phaseBurst.offsetWidth, height: phaseBurst.offsetHeight },
    )
    phaseBurst.style.setProperty('--jc-phase-fit', scale.toFixed(3))
  }

  function showPhase(which: PhaseStamp): void {
    // 문구 세트는 꾸밈이다(로드맵 M9). 뽑은 것이 없으면 기본 키로 떨어진다(`ui/cosmeticVars.phaseStampKey`).
    phaseText.textContent = t(phaseStampKey(which))
    /* 종류가 곧 얼굴이다. 기울기·진입 배율·워시 농도를 `juice.css`의 modifier가 정한다. */
    phaseLayer.className = `jc-phase jc-phase--${which}`
    /* 재기 전에 되돌린다. 지난 문구의 배율이 남아 있으면 이번 문구를 그 배율로 재게 된다. */
    phaseBurst.style.setProperty('--jc-phase-fit', '1')
    /* 순서가 규칙이다. 클래스가 **없는 상태로** 한 번 플러시해야 애니메이션이 처음부터 돈다. */
    restartAnimation(phaseLayer)
    phaseLayer.classList.add('is-on')
    /* 켠 다음에 잰다. 꺼져 있는 동안에는 `display: none`이라 잴 크기가 없다. */
    fitPhase()
    /*
     * 이 레이어에서 탭을 받는 유일한 순간. 받는 이유는 두 가지이고 둘 다 게임을 위한 것이다 -
     * 아는 사람이 자막을 즉시 걷을 수 있어야 하고, 그 탭이 그대로 방에 떨어져
     * **수색 첫 발을 허공에 쓰는 일**(오발 = 탄환 손실)이 없어야 한다.
     */
    phaseLayer.style.pointerEvents = 'auto'
    phaseTimer = window.setTimeout(endPhase, PHASE_MS)
  }

  function phase(which: PhaseStamp, phaseOptions: PhaseStampOptions = {}): void {
    /* 앞의 예약을 먼저 끝낸다. 그 자리에서 앞의 콜백이 불리므로 약속은 그대로 지켜진다. */
    endPhase()

    const done = phaseOptions.onDone
    /*
     * 꺼져 있으면 자막은 생략하되 **콜백은 즉시 부른다.** 다른 연출들은 그냥 돌아가면 그만이지만
     * 이 자막은 제한 시간을 쥐고 있어서, 조용히 돌아가는 것이 곧 시계가 안 돌아가는 것이다.
     */
    if (!visible) {
      done?.()
      return
    }

    phaseDone = done ?? null

    const requested = phaseOptions.delayMs ?? 0
    const delay = Number.isFinite(requested) ? Math.max(0, requested) : 0
    if (delay === 0) {
      showPhase(which)
      return
    }

    phaseTimer = window.setTimeout(() => {
      phaseTimer = 0
      showPhase(which)
    }, delay)
  }

  /* 떠 있는 동안 화면 아무 데나 누르면 걷힌다. 말풍선만이 아니라 레이어 전체가 과녁이다. */
  phaseLayer.addEventListener('pointerdown', endPhase)

  /* --- 공개 동작 ----------------------------------------------------------- */

  function hit(clientX: number, clientY: number, juice: HitJuice): void {
    if (!visible) return

    /*
     * 남의 발견. 이름표 한 장으로 끝난다 - 점수·배수·펀치는 내 것이 아니다(`HitJuice.by` 주석).
     * 기울기 표는 그대로 돈다(`stampNode`) - 같은 자리에 두 사람이 연달아 찍으면 겹쳐 보이지 않게.
     */
    if (juice.by !== undefined) {
      spawn(stampNode(juice.by, 'jc-stamp--by', POP_BASE), STAMP_MS, clientX, clientY)
      return
    }

    const step = safeStep(juice.comboStep)
    const multiplier = juice.multiplier ?? 1

    if (juice.caption !== false) {
      const stamp = stampNode(t(`juice.hit.${comboTier(step)}`), 'jc-stamp--hit', popScale(step))
      if (multiplier >= MULTIPLIER_MIN) {
        const chip = juice.atCap === true ? 'jc-stamp__mult is-max' : 'jc-stamp__mult'
        stamp.append(createElement('div', chip, formatMultiplier(multiplier)))
      }
      spawn(stamp, STAMP_MS, clientX, clientY)
    }

    if (juice.score !== undefined) {
      const score = createElement('div', 'jc-score', formatScore(juice.score))
      spawn(score, SCORE_MS, clientX, clientY)
    }

    punch(step)
  }

  function miss(clientX: number, clientY: number, juice: MissJuice = {}): void {
    if (!visible) return

    if (juice.flash !== false) {
      flash.classList.remove('is-on')
      restartAnimation(flash)
      flash.classList.add('is-on')
      schedule('flash', () => flash.classList.remove('is-on'), FLASH_MS)
    }

    if (juice.comboBroken === true) {
      const stamp = stampNode(t('juice.comboBreak'), 'jc-stamp--break', POP_BASE)
      spawn(stamp, BREAK_MS, clientX, clientY)
    }

    shake()
  }

  function finale(juice: FinaleJuice = {}): void {
    if (!visible) return

    const perfect = juice.perfect === true
    boardTitle.textContent = t(perfect ? 'juice.perfect.title' : 'juice.finale.title')

    /*
     * 아랫줄은 세 갈래다. 보너스가 있으면 보너스가 이긴다 - 퍼펙트 화면에서 사람이 가장
     * 알고 싶은 것은 "그래서 얼마나 이득인가"이고, 개수는 결과 화면이 곧바로 다시 말해 준다.
     */
    if (juice.bonus !== undefined) {
      boardSub.textContent = t('juice.perfect.bonus', { bonus: formatScore(juice.bonus) })
    } else if (perfect) {
      boardSub.textContent = t('juice.perfect.sub')
    } else {
      boardSub.textContent = t('juice.finale.sub', { count: juice.foundCount ?? 0 })
    }

    board.className = perfect ? 'jc-board jc-board--perfect' : 'jc-board'
    restartAnimation(board)
    board.classList.add('is-on')
    schedule('board', () => board.classList.remove('is-on'), perfect ? PERFECT_MS : FINALE_MS)
  }

  function nobodySaw(): void {
    if (!visible) return
    nobodyTitle.textContent = t('juice.nobodySaw.title')
    nobodySub.textContent = t('juice.nobodySaw.sub')
    // 기울기는 스탬프와 같은 표를 돈다 - 같은 손이 찍은 도장이어야 한다(규칙 3, 난수 아님).
    nobody.style.setProperty('--jc-tilt', `${stampTilt(tiltIndex)}deg`)
    tiltIndex += 1
    restartAnimation(nobody)
    nobody.classList.add('is-on')
    schedule('nobody', () => nobody.classList.remove('is-on'), NOBODY_SAW_MS)
  }

  function clear(): void {
    for (const entry of [...live]) drop(entry)
    clearSlots()
    /*
     * 페이즈 자막은 예약까지 데려간다. 방 전환을 기다리던 자막이 라운드가 접힌 뒤에
     * 뒤늦게 튀어나오는 것을 막는 자리이고, 동시에 **여기서 완료 콜백이 불린다** -
     * 호출부는 "자막이 취소됐으니 시계가 안 돌아간다"를 걱정하지 않아도 된다.
     */
    endPhase()
    flash.classList.remove('is-on')
    board.classList.remove('is-on')
    nobody.classList.remove('is-on')
    stopBorrowed()
  }

  return {
    hit,
    miss,
    finale,
    nobodySaw,
    phase,
    clear,

    setVisible(next: boolean): void {
      /*
       * 끌 때 진행 중이던 연출을 함께 버린다. 안 그러면 배치 모드를 다녀왔을 때
       * 몇 초 전에 쏜 자막이 아직 떠 있는 유령 상태가 된다.
       */
      if (!next) clear()
      visible = next
      root.hidden = !next
    },

    dispose(): void {
      clear()
      root.remove()
    },
  }
}
